import {
  buildProductReportRows,
  renderDataGridLocaleText,
  formatProjectCompanyCodes,
  normalizeProjectCompanyCodes,
} from 'utils';
import {
  DataGridBox,
  BankUsageChip,
  CustomToolbar,
  ZebraDataGrid,
  ShowProductBanks,
  BankStatusSummary,
  getBankStatusLabel,
  CustomNoRowsOverlay,
  ProductCompanyCodesDialog,
  getProductCompanyStatusLabel,
} from 'components';
import type { IntlShape } from 'react-intl';
import apiService from 'library/api-service';
import { useEffect, useMemo, useState } from 'react';
import type { ProductReportRow } from 'library/types';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import usedBanksActions from 'store/features/used-banks/actions';
import { Alert, Box, Chip, Tooltip, Typography } from '@mui/material';
import { BOOK_BANK_STATUS_DISPLAY_ORDER, EXPORT_TYPE_ENUM } from 'enums';
import environmentTypesActions from 'store/features/environment-types/actions';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty } from 'store/features/used-banks/selector';
import { selectEnvironmentTypes, selectEnvironmentTypesIsEmpty } from 'store/features/environment-types/selector';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-premium';

const VERTICALLY_CENTERED_CELL_CLASS = 'product-report-vertically-centered-cell';

const formatStatusSummary = (row: ProductReportRow, intl: IntlShape): string => {
  const labels = BOOK_BANK_STATUS_DISPLAY_ORDER.map((status) => ({
    status,
    label: getBankStatusLabel(status, intl),
  }));

  return labels
    .filter(({ status }) => row.bankStatusCounts[status] > 0)
    .map(({ status, label }) => `${label}: ${row.bankStatusCounts[status]}`)
    .join(', ');
};

const formatCompanyCodes = (value: unknown, intl: IntlShape): string =>
  formatProjectCompanyCodes(value, (status) => getProductCompanyStatusLabel(status, intl));

const ProductReport = () => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const usedBanks = useAppSelector(selectUsedBanks);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);
  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProductReportRow[]>([]);
  const [selectedBankRow, setSelectedBankRow] = useState<ProductReportRow | null>(null);
  const [selectedCompanyCodesRow, setSelectedCompanyCodesRow] = useState<ProductReportRow | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const incompleteAdaptation = isEmptyProducts || isEmptyEnvironmentTypes;

  const columns: GridColDef<ProductReportRow>[] = useMemo(
    () => [
      {
        field: 'projectName',
        flex: 1,
        minWidth: 190,
        headerName: intl.formatMessage({ id: 'report.companyName', defaultMessage: 'Proje Adı' }),
        cellClassName: VERTICALLY_CENTERED_CELL_CLASS,
      },
      {
        field: 'projectId',
        flex: 0.8,
        minWidth: 160,
        headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        cellClassName: VERTICALLY_CENTERED_CELL_CLASS,
        valueFormatter: (value: number) => products.find((product) => product.id === value)?.title ?? `#${value}`,
        type: 'singleSelect',
        valueOptions: () => products.map((product) => ({ value: product.id, label: product.title })),
      },
      {
        field: 'environmentTypeId',
        flex: 0.5,
        minWidth: 100,
        headerName: intl.formatMessage({ id: 'project.table.environmentType', defaultMessage: 'Sunucu Tipi' }),
        headerAlign: 'center',
        cellClassName: VERTICALLY_CENTERED_CELL_CLASS,
        valueFormatter: (value: number | null) =>
          environmentTypes.find((environmentType) => environmentType.id === value)?.title ?? '',
        type: 'singleSelect',
        valueOptions: () =>
          environmentTypes.map((environmentType) => ({ value: environmentType.id, label: environmentType.title })),
      },
      {
        field: 'companyCodes',
        flex: 0.45,
        minWidth: 140,
        headerName: intl.formatMessage({
          id: 'productReport.table.companyCodeCount',
          defaultMessage: 'Şirket Kodları',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'string',
        valueGetter: (value) => formatCompanyCodes(value, intl),
        renderCell: (params: GridRenderCellParams<ProductReportRow, string>) => {
          const codes = normalizeProjectCompanyCodes(params.row.companyCodes);
          const countLabel = intl.formatMessage(
            { id: 'report.companyCodeCount', defaultMessage: '{count} şirket kodu' },
            { count: codes.length }
          );
          const actionLabel = intl.formatMessage({
            id: 'project.table.showCompanyCodes',
            defaultMessage: 'Şirket Kodlarını Göster',
          });

          return (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
              <Tooltip title={actionLabel}>
                <Chip
                  icon={<BusinessIcon fontSize="small" />}
                  label={countLabel}
                  size="small"
                  variant={codes.length > 0 ? 'filled' : 'outlined'}
                  color={codes.length > 0 ? 'primary' : 'default'}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedCompanyCodesRow(params.row);
                  }}
                  aria-label={`${actionLabel}: ${countLabel}`}
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'bankUsage',
        minWidth: 50,
        headerName: intl.formatMessage({ id: 'productReport.table.bankUsage', defaultMessage: 'Banka Kullanımı' }),
        headerAlign: 'center',
        align: 'center',
        type: 'string',
        valueGetter: (_, row) => `${row.bankLimitUsageCount} / ${row.bankCount}`,
        sortComparator: (first, second) => Number(first.split('/')[0]) - Number(second.split('/')[0]),
        renderCell: (params: GridRenderCellParams<ProductReportRow, string>) => (
          <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
            <BankUsageChip
              assignmentCount={params.row.bankAssignmentCount}
              bankLimitUsageCount={params.row.bankLimitUsageCount}
              bankLimit={params.row.bankCount}
              actionLabel={intl.formatMessage({
                id: 'report.showBankDistribution',
                defaultMessage: 'Banka dağılımını göster',
              })}
              onClick={() => setSelectedBankRow(params.row)}
            />
          </Box>
        ),
      },
      {
        field: 'bankStatuses',
        flex: 1.2,
        minWidth: 450,
        sortable: false,
        headerName: intl.formatMessage({ id: 'productReport.table.bankStatuses', defaultMessage: 'Banka Durumları' }),
        headerAlign: 'center',
        valueGetter: (_, row) => formatStatusSummary(row, intl),
        valueFormatter: (value: string) => value,
        renderCell: (params: GridRenderCellParams<ProductReportRow>) => (
          <Box display="flex" alignItems="center" width="100%" height="100%" py={0.75}>
            <BankStatusSummary counts={params.row.bankStatusCounts} />
          </Box>
        ),
      },
    ],
    [environmentTypes, intl, products]
  );

  const getValues = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBooks();

      if (response.isSuccess) {
        setData(buildProductReportRows(response.data));
      }
    } catch {
      // HTTP errors are surfaced by the shared response interceptor.
    } finally {
      setLoading(false);
    }
  };

  const onClickDownloadHandler = async (exportType: EXPORT_TYPE_ENUM) => {
    const fileName = intl.formatMessage({ id: 'productReport.exportFileName', defaultMessage: 'urun-raporu' });

    if (exportType === EXPORT_TYPE_ENUM.EXCEL) {
      await apiRef.current.exportDataAsExcel({ fileName });
      return;
    }

    apiRef.current.exportDataAsCsv({ fileName, utf8WithBom: true });
  };

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    getValues();
  }, []);

  return incompleteAdaptation ? (
    <Alert severity="info" icon={false}>
      <Typography>
        <FormattedMessage
          id="productReport.completeAdaptationsText"
          defaultMessage="Sayfayı görüntülemek için lütfen ürün ve sunucu tipi uyarlamalarını tamamlayın."
        />
      </Typography>
    </Alert>
  ) : (
    <>
      <ProductCompanyCodesDialog
        open={Boolean(selectedCompanyCodesRow)}
        onClose={() => setSelectedCompanyCodesRow(null)}
        companyCodes={normalizeProjectCompanyCodes(selectedCompanyCodesRow?.companyCodes)}
        allCompanies={selectedCompanyCodesRow?.companies ?? []}
        productTitle={
          selectedCompanyCodesRow
            ? (products.find((product) => product.id === selectedCompanyCodesRow.projectId)?.title ??
              `#${selectedCompanyCodesRow.projectId}`)
            : undefined
        }
        searchable
      />
      <ShowProductBanks
        open={Boolean(selectedBankRow)}
        handleClose={() => setSelectedBankRow(null)}
        projects={selectedBankRow ? [selectedBankRow.bookProject] : []}
        allProducts={products}
        usedBanks={usedBanks}
        bookTitle={selectedBankRow?.projectName}
      />
      <DataGridBox>
        <ZebraDataGrid<ProductReportRow>
          apiRef={apiRef}
          pagination
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => setRowSelectionModel(newRowSelectionModel)}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          disableRowSelectionOnClick
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          getRowHeight={() => 'auto'}
          sx={{
            [`& .${VERTICALLY_CENTERED_CELL_CLASS}`]: {
              display: 'flex',
              alignItems: 'center',
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () =>
              CustomToolbar({
                multiExport: true,
                exportAsExcelHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.EXCEL),
                exportAsCsvHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.CSV),
              }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ProductReport;
